import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import { BottomNav } from 'components'
import GlobalSearch from 'components/UnvUI/GlobalSearch'
import { clearUser } from 'store/slices/AuthSlice'
import { recordLogout } from 'service/AuditLogoutService'
import { changeThemeAction, removeUser } from 'store/slices/UserSlice'
import { filterSidebarByRoles } from 'constants/sidebarLinks'
import { NAV_SECTIONS, ROLE_LABELS, roleCodes, STUDENT_ROLES } from 'utils/accessPolicy'
import { homePathByRoles } from 'utils/roleRedirect'
import useInstitution, { INSTITUTIONS } from 'hooks/useInstitution'
import { ChangeTheme } from 'service/ThemeService'
import LangSwitcher from 'components/LangSwitcher/LangSwitcher'
import CurrentNotifications from 'components/Notification/CurrentNotifications'
import { LayoutContext } from '../LayoutContext'
import styles from './UnvLayout.module.scss'

const ICONS = {
  '/home/': 'home', '/admin/': 'cog', '/students-list/': 'users', '/employees/': 'briefcase',
  '/admission/': 'user-plus', '/employee-wizard/': 'user-plus', '/transfers/': 'arrow-right',
  '/orders/': 'file-text', '/institution-structure/': 'layers', '/groups/': 'group',
  '/syllabuses/': 'book-open', '/syllabus-builder/': 'edit-2', '/syllabus-approval/': 'check-square',
  '/schedule/': 'calendar', '/schedule-builder/': 'calendar-plus', '/schedule-import/': 'cloud-upload',
  '/sessions/': 'exam', '/reports/': 'bar', '/accounting/': 'wallet', '/archive/': 'archive',
  '/journal/': 'journal', '/gradebook/': 'gradebook', '/assignments-list/': 'assignment',
  '/tests/': 'test', '/library/': 'library', '/dorm/': 'dorm', '/practice/': 'briefcase',
  '/diploma/': 'graduation', '/tasks/': 'check-square', '/notes/': 'edit-2', '/calendar/': 'calendar',
  '/chats/': 'message-circle', '/statements/': 'file-text', '/structure/': 'layers', '/help/': 'help-circle',
  '/my-disciplines/': 'book-open', '/my-syllabus/': 'book-open', '/personalcard/': 'user',
}
const iconFor = (path) => ICONS[path] || 'list'
const EMPTY_ROLES = []

function UnvLayout({ children, title }) {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const previousPath = useRef(location.pathname)
  const auth = useSelector((state) => state.auth || {})
  const me = auth.me
  const roles = auth.roles || EMPTY_ROLES
  const codes = useMemo(() => roleCodes(roles), [roles])
  const { institution, institutionInfo, setInstitution } = useInstitution()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menuQuery, setMenuQuery] = useState('')
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') === 'dark' ? 'dark' : 'light')
  const menuButtonRef = useRef(null)
  const isLearner = !me?.is_superuser && codes.length > 0 && codes.every((code) => [...STUDENT_ROLES, 'parent', 'applicant'].includes(code))

  useEffect(() => {
    if (isLearner && me?.institution && me.institution !== institution) setInstitution(me.institution)
  }, [isLearner, me?.institution, institution, setInstitution])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useLayoutEffect(() => {
    if (previousPath.current === location.pathname) return
    previousPath.current = location.pathname
    if (navigationType !== 'POP') window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [location.pathname, navigationType])

  useEffect(() => {
    if (!mobileOpen) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event) => {
      if (event.key === 'Escape') { setMobileOpen(false); menuButtonRef.current?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = previous; document.removeEventListener('keydown', onKey) }
  }, [mobileOpen])

  const toggleTheme = () => ChangeTheme().then(({ data }) => {
    setTheme(data.them ? 'light' : 'dark')
    dispatch(changeThemeAction(data))
  })

  const sidebarItems = useMemo(() => {
    const items = filterSidebarByRoles(codes, { isSuper: !!me?.is_superuser })
    const home = me?.is_superuser ? '/admin/' : homePathByRoles(roles, auth.primaryRole?.code || auth.primaryRole)
    return items.map((item) => item.to === '/home/' ? { ...item, to: home === '/login' ? '/home/' : home, text: 'Рабочее пространство', key: null } : item)
      .filter((item, index, all) => all.findIndex((other) => other.to === item.to) === index)
  }, [codes, me?.is_superuser, roles, auth.primaryRole])

  const visibleGroups = useMemo(() => Object.keys(NAV_SECTIONS).map((section) => ({
    section, label: NAV_SECTIONS[section],
    items: sidebarItems.filter((item) => item.section === section && t(item.key || '', item.text).toLowerCase().includes(menuQuery.toLowerCase())),
  })).filter((group) => group.items.length), [sidebarItems, menuQuery, t])

  const roleCode = auth.primaryRole?.code || (typeof auth.primaryRole === 'string' ? auth.primaryRole : null) || codes[0]
  const roleLabel = me?.is_superuser ? 'Суперпользователь' : ROLE_LABELS[roleCode] || auth.primaryRole?.title || 'Сотрудник'
  const fullName = me?.full_name || [me?.last_name, me?.first_name].filter(Boolean).join(' ') || me?.username || ''
  const initials = fullName.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'ЧУ'
  const currentItem = sidebarItems.find((item) => location.pathname === item.to || (item.to !== '/admin/' && location.pathname.startsWith(item.to)))
  const taskDetailTitle = { '/task/': t('tasksWorkspace.taskDetail', 'Задача'), '/subtask/': t('tasksWorkspace.subtaskDetail', 'Подзадача'), '/task-tree/': t('tasksWorkspace.taskStructure', 'Структура задачи') }[location.pathname]
  const pageTitle = title || taskDetailTitle || (location.pathname.startsWith('/personalcard') ? t('profileAccount.heading', 'Мой профиль') : currentItem ? t(currentItem.key || '', currentItem.text) : 'Рабочее пространство')
  useEffect(() => { document.title = `${pageTitle} · ${institutionInfo.name}` }, [pageTitle, institutionInfo.name])
  const mobileItems = sidebarItems.slice(0, 4).map((item) => ({ key: item.to, path: item.to, label: t(item.key || '', item.text), icon: iconFor(item.to) }))
  mobileItems.push({ key: 'profile', path: '/personalcard/', label: 'Профиль', icon: 'user' })

  const handleLogout = async () => {
    if (!(await recordLogout())) return
    for (const key of ['token', 'refresh', 'login_roles', 'login_primary_role', 'user', 'auth_session']) localStorage.removeItem(key)
    dispatch(clearUser())
    dispatch(removeUser())
    navigate('/login', { replace: true })
  }

  return (
    <LayoutContext.Provider value={{ inLayout: true, institution }}>
      <div className={styles.shell}>
        <a className={styles.skipLink} href="#workspace-content">К содержимому</a>
        {mobileOpen && <button type="button" className={styles.mobileOverlay} onClick={() => setMobileOpen(false)} aria-label="Закрыть меню" />}
        <aside id="workspace-menu" className={`${styles.sidebar} ${collapsed ? styles.sidebar_collapsed : ''} ${mobileOpen ? styles.sidebar_mobileOpen : ''}`}>
          <div className={styles.brand}>
            <span className={styles.brand_logo}><img src={institutionInfo.logo} alt={institutionInfo.logoAlt} className={styles.brand_logo_img} /></span>
            {!collapsed && <div className={styles.brand_name}><strong>{institutionInfo.name}</strong><span>{institutionInfo.subtitle}</span></div>}
            <button type="button" className={styles.collapseBtn} onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}><Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={14} /></button>
            <button type="button" className={styles.mobileClose} onClick={() => setMobileOpen(false)} aria-label="Закрыть меню"><Icon name="x" size={20} /></button>
          </div>
          {!collapsed && <div className={styles.institutionCard}>
            <span className={styles.eyebrow}>Учреждение</span>
            {isLearner ? <strong>{institutionInfo.label}</strong> : <div className={styles.institutionSwitch} role="group" aria-label="Выбор учреждения">
              {Object.entries(INSTITUTIONS).map(([value, info]) => <button type="button" key={value} aria-pressed={institution === value} className={institution === value ? styles.institutionActive : ''} onClick={() => setInstitution(value)}>{info.label}</button>)}
            </div>}
            <span className={styles.institutionLevel}>{institutionInfo.level}</span>
          </div>}
          {!collapsed && <label className={styles.menuSearch}><Icon name="search" size={16} /><input type="search" placeholder="Найти раздел" aria-label="Найти раздел меню" value={menuQuery} onChange={(event) => setMenuQuery(event.target.value)} /></label>}
          <nav className={styles.menu} aria-label="Главное меню">
            {visibleGroups.map((group) => <div className={styles.menuGroup} key={group.section}>
              {!collapsed && <div className={styles.groupTitle}>{group.label}</div>}
              {group.items.map((item) => {
                const label = item.key ? t(item.key, item.text) : item.text
                return <NavLink key={item.to} to={item.to} end onClick={() => setMobileOpen(false)} title={collapsed ? label : undefined} className={({ isActive }) => `${styles.link} ${isActive ? styles.link_active : ''}`}>
                  <span className={styles.linkIcon}><Icon name={iconFor(item.to)} size={18} color="currentColor" /></span>
                  {!collapsed && <span className={styles.linkText}>{label}</span>}
                </NavLink>
              })}
            </div>)}
            {!visibleGroups.length && !collapsed && <p className={styles.emptyMenu}>Разделы не найдены</p>}
          </nav>
          <div className={styles.user}>
            <div className={styles.userAvatar} aria-hidden="true">{initials}</div>
            {!collapsed && <div className={styles.userInfo}><div className={styles.user_name}>{fullName}</div><div className={styles.user_role}>{roleLabel}</div></div>}
            <button type="button" className={styles.logoutButton} onClick={handleLogout} title="Выйти" aria-label="Выйти"><Icon name="logout" size={18} /></button>
          </div>
        </aside>
        <div className={styles.main}>
          <header className={styles.topbar}>
            <button ref={menuButtonRef} type="button" className={styles.mobileToggle} onClick={() => { setCollapsed(false); setMobileOpen((value) => !value) }} aria-expanded={mobileOpen} aria-controls="workspace-menu" aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}><Icon name={mobileOpen ? 'x' : 'menu'} size={22} /></button>
            <div className={styles.topbar_title}><span className={styles.breadcrumb}>{institutionInfo.label}<span className={styles.headerRole}> / {roleLabel}</span></span><strong title={pageTitle}>{pageTitle}</strong></div>
            <div className={styles.topbar_right}>
              {!isLearner && <GlobalSearch />}
              <CurrentNotifications />
              <LangSwitcher />
              <button type="button" className={styles.topbarIconBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'} aria-label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}><Icon name={theme === 'dark' ? 'sun' : 'moon'} size={20} /></button>
              <button type="button" className={styles.profileButton} onClick={() => navigate('/personalcard/')} title="Профиль" aria-label="Профиль">{initials}</button>
            </div>
          </header>
          <main id="workspace-content" className={styles.content} key={institution}>{children}</main>
          <footer className={styles.workspaceFooter}><span>{institutionInfo.name} · {institutionInfo.level}</span><span>Единое рабочее пространство</span></footer>
        </div>
        <BottomNav items={mobileItems} hidden={mobileOpen} />
      </div>
    </LayoutContext.Provider>
  )
}
export default UnvLayout
